import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

export interface ErrorInfo {
  componentStack?: string;
  errorBoundary?: string;
  errorBoundaryStack?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean;
  level?: 'app' | 'route' | 'component';
}

export interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
  level?: 'app' | 'route' | 'component';
  eventId?: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Não capturar erros de API - deixar componentes tratarem
    if (error && typeof error === 'object') {
      // Check for API errors (have status or code properties)
      const hasStatus = 'status' in error;
      const hasCode = 'code' in error;
      const hasErrorObject = 'error' in error && typeof error.error === 'object';

      // Check if it's a session expiration error
      const isSessionError = hasErrorObject &&
        error.error &&
        typeof error.error === 'object' &&
        'code' in error.error &&
        (error.error.code === 'SESSION_EXPIRED' || error.error.code === 'UNAUTHORIZED');

      if (hasStatus || hasCode || isSessionError) {
        return { hasError: false }; // Não mostra a tela de erro para erros de API
      }
    }

    return {
      hasError: true,
      error,
      eventId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Não capturar erros de API - deixar componentes tratarem
    if (error && typeof error === 'object') {
      const hasStatus = 'status' in error;
      const hasCode = 'code' in error;
      const hasErrorObject = 'error' in error && typeof error.error === 'object';
      const isSessionError = hasErrorObject &&
        error.error &&
        typeof error.error === 'object' &&
        'code' in error.error &&
        (error.error.code === 'SESSION_EXPIRED' || error.error.code === 'UNAUTHORIZED');

      if (hasStatus || hasCode || isSessionError) {
        return; // Não processa erros de API
      }
    }

    const reactErrorInfo = errorInfo as React.ErrorInfo & { errorBoundaryStack?: string };
    const enhancedErrorInfo: ErrorInfo = {
      componentStack: reactErrorInfo.componentStack ?? undefined,
      errorBoundary: this.constructor.name,
      errorBoundaryStack: reactErrorInfo.errorBoundaryStack,
    };

    this.setState({
      errorInfo: enhancedErrorInfo,
    });

    if (this.props.onError) {
      this.props.onError(error, enhancedErrorInfo);
    }

    this.reportError(error, enhancedErrorInfo);

  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      level: this.props.level ?? 'component',
      eventId: this.state.eventId,
    };

    void errorReport;
    
  };

  private resetError = () => {
    if (this.resetTimeoutId) {
      window.clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback ?? ErrorFallback;
      
      return (
        <FallbackComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
          level={this.props.level}
          eventId={this.state.eventId}
        />
      );
    }

    return this.props.children;
  }
}

export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const ComponentWithErrorBoundary = (props: P) => (
    <ErrorBoundary {...options}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Component'
  })`;

  return ComponentWithErrorBoundary;
};

export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const handleError = React.useCallback((error: Error | string) => {
    if (typeof error === 'string') {
      setError(new Error(error));
    } else {
      setError(error);
    }
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return handleError;
};